import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = { class: "flex flex-col gap-1" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", { class: "text-[11px] uppercase tracking-wider text-neutral50" }, " Período (horário de Brasília) ", -1)
const _hoisted_4 = { class: "flex flex-wrap gap-1" }
const _hoisted_5 = ["aria-pressed", "data-testid", "onClick"]
const _hoisted_6 = ["title", "data-testid"]
const _hoisted_7 = ["data-testid"]
const _hoisted_8 = { class: "flex items-center gap-1" }
const _hoisted_9 = ["data-testid"]
const _hoisted_10 = { class: "flex flex-col gap-1" }
const _hoisted_11 = ["for"]
const _hoisted_12 = ["id", "value", "data-testid"]
const _hoisted_13 = { class: "flex flex-col gap-1" }
const _hoisted_14 = /*#__PURE__*/_createElementVNode("span", { class: "text-[11px] uppercase tracking-wider text-neutral50" }, "Atalhos", -1)
const _hoisted_15 = { class: "flex flex-wrap gap-1" }
const _hoisted_16 = ["aria-label", "disabled", "aria-busy", "data-testid"]
const _hoisted_17 = ["data-testid"]
const _hoisted_18 = ["aria-pressed", "data-testid", "onClick"]
const _hoisted_19 = /*#__PURE__*/_createElementVNode("span", { class: "flex-1" }, null, -1)
const _hoisted_20 = { class: "flex items-center gap-2 text-xs text-neutral50" }
const _hoisted_21 = { key: 0 }
const _hoisted_22 = ["disabled", "data-testid"]
const _hoisted_23 = ["disabled", "data-testid"]

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import DateFilter from '@/components/Filters/DateFilter.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import { useErrorToast } from '@/composables/useErrorToast'
import Network from '@/Network'
import FieldOptionsService from '@/Network/Services/FieldOptionsService'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { parseLocalDate, toDateOnly } from '@/utils/date'
import type {
  PriorityCheckStatus,
  PriorityDoctorFacetDto,
  PriorityPositionOption,
  PrioritySectorOption,
  PrioritySituation,
  PrioritySituationCounts,
  PrioritySlaState
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { PRIORITY_CHECK_LABELS, PRIORITY_SITUATION_GROUPS } from '../constants'
import {
  buildPositionSelectOptions,
  buildSectorSelectOptions,
  prunedSectorsForUnits,
  selectedPositionValues,
  toggleGroup,
  togglePositionOption
} from '../filterOptions'
import {
  PRIORITY_MIN_REJECTIONS,
  PRIORITY_PERIOD_PRESETS,
  PRIORITY_SLA_STATES,
  PRIORITY_STALLED_HOURS,
  lastSevenDaysRange
} from '../filters'
import { PRIORITY_SLA_PENDING_STATES, PRIORITY_SLA_STATE_LABELS, formatSlaHour } from '../slaFormat'
import type { PriorityPaymentsFilters, PriorityPeriodPreset } from '../filters'

interface QuickFilter {
  key: string
  label: string
  isOn: (filters: PriorityPaymentsFilters) => boolean
  toggle: (filters: PriorityPaymentsFilters) => Partial<PriorityPaymentsFilters>
}

const PENDING_OPTION = 'PENDING'

const SEARCH_DEBOUNCE_MS = 300

export default /*#__PURE__*/_defineComponent({
  __name: 'PriorityPaymentsFilterBar',
  props: {
    filters: {},
    cycleLabel: {},
    listedUntil: { default: null },
    lastLoadedLabel: {},
    isRefreshing: { type: Boolean },
    isExporting: { type: Boolean },
    inferredOperationalGroup: { default: null },
    doctorOptions: { default: () => [] },
    situationCounts: { default: null },
    antecipePaidUnconfirmedCount: { default: null },
    testId: { default: 'priority-payments-filter-bar' }
  },
  emits: ["update:filters", "refresh", "export", "export-antecipe-paid-unconfirmed"],
  setup(__props: any, { emit }) {

const props = __props;





const antecipePaidUnconfirmedLabel = computed(() =>
  props.antecipePaidUnconfirmedCount === null ? '—' : String(props.antecipePaidUnconfirmedCount)
)

/**
 * Sufixo do rótulo do "Ciclo atual" (B1, 25/09): a lista também traz os plantões
 * À vista do próximo ciclo já encerrados há N min, e o rótulo do ciclo sozinho
 * não explica essas linhas. Hora em Brasília, como o rótulo do período.
 */
const listedUntilText = computed(() => {
  if (props.filters.preset !== 'CURRENT' || !props.listedUntil) return null
  const hour = formatSlaHour(props.listedUntil)
  return hour === '—' ? null : `+ plantões À vista encerrados até ${hour}`
})

const updateFilters = (patch: Partial<PriorityPaymentsFilters>): void => {
  emit('update:filters', patch)
}

/**
 * O mockup tinha três opções; `PENDING` existe em PROD e o dono mandou
 * oferecê-lo — esconder um estado que chega nas linhas deixaria um recorte
 * inalcançável pelo filtro. Os rótulos são os mesmos de `PRIORITY_CHECK_LABELS`.
 */
const CHECK_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Qualquer' },
  { value: 'NONE', label: PRIORITY_CHECK_LABELS.NONE },
  { value: 'PARTIAL', label: PRIORITY_CHECK_LABELS.PARTIAL },
  { value: 'PENDING', label: PRIORITY_CHECK_LABELS.PENDING },
  { value: 'OK', label: PRIORITY_CHECK_LABELS.OK }
]

const QUICK_FILTERS: QuickFilter[] = [
  {
    key: 'rejections',
    label: '2+ rejeições',
    isOn: (f) => f.minRejections !== null,
    toggle: (f) => ({ minRejections: f.minRejections === null ? PRIORITY_MIN_REJECTIONS : null })
  },
  {
    key: 'nouser',
    label: 'Médico sem usuário',
    isOn: (f) => f.doctorWithoutUser,
    toggle: (f) => ({ doctorWithoutUser: !f.doctorWithoutUser })
  },
  {
    key: 'stalled',
    label: 'Parado há +24 h',
    isOn: (f) => f.stalledHours !== null,
    toggle: (f) => ({ stalledHours: f.stalledHours === null ? PRIORITY_STALLED_HOURS : null })
  }
]

/**
 * Legado: links antigos com `critical=true` ainda ligam `onlyCritical`. O chip
 * só aparece enquanto o filtro está ligado, para a pessoa ver e poder desligar.
 */
const LEGACY_CRITICAL_FILTER: QuickFilter = {
  key: 'critical',
  label: 'Só críticos',
  isOn: (f) => f.onlyCritical,
  toggle: (f) => ({ onlyCritical: !f.onlyCritical })
}

const visibleQuickFilters = computed<QuickFilter[]>(() =>
  props.filters.onlyCritical ? [...QUICK_FILTERS, LEGACY_CRITICAL_FILTER] : QUICK_FILTERS
)

/**
 * Combos de unidade e escala pelas rotas de filtro do PRÓPRIO ds-attendance
 * (`operations/filters/*`), as mesmas da fila de Registro de Plantão. O escopo
 * delas é `ds_attendance#attendance_emitting` via `@AccessibleResources`, o
 * MESMO da rota de pagamentos prioritários. `/healthUnits` e `/schedules`
 * exigem leitura de unidade/escala e davam 403 ao receptor puro.
 */
const dsAttendanceStore = useDsAttendanceStore()

const openFilter = ref<string | null>(null)
const groupOptions = ref<{ label: string; value: string }[]>([])

const toggleOpen = (key: string): void => {
  openFilter.value = openFilter.value === key ? null : key
}

const chipClass = (isOn: boolean): string =>
  isOn ? 'border-primary bg-primary text-white' : 'border-lightGray30 bg-white text-neutral50'

const unitOptions = computed(() =>
  dsAttendanceStore.filterHealthUnits.map((unit) => ({
    label: unit.title,
    value: String(unit.value)
  }))
)

const { notifyError, notifyErrorMessage } = useErrorToast()

/**
 * Frente G: opções de Setor e Posição pela rota do PRÓPRIO módulo
 * (`GET /priority-payments/filter-options`, escopo `attendance_emitting` = o
 * da listagem). Não recortadas por dado: a opção não some por não ter sido escolhida.
 */
const sectorOptions = ref<PrioritySectorOption[]>([])
const positionOptions = ref<PriorityPositionOption[]>([])

const sectorSelectOptions = computed(() =>
  buildSectorSelectOptions(sectorOptions.value, props.filters.healthUnits, props.filters.sectors)
)
const positionSelectOptions = computed(() =>
  buildPositionSelectOptions(positionOptions.value, props.filters.positions)
)
const selectedPositions = computed(() =>
  selectedPositionValues(positionOptions.value, props.filters.positions)
)

const scheduleOptions = computed(() =>
  dsAttendanceStore.filterSchedules.map((schedule) => ({
    label: schedule.title,
    value: String(schedule.value)
  }))
)

/** Reidrata o seletor com o intervalo vindo da URL (`preset=CUSTOM&startAt&endAt`). */
const customRange = computed(() =>
  props.filters.startAt && props.filters.endAt
    ? { start: new Date(props.filters.startAt), end: new Date(props.filters.endAt) }
    : null
)

function onPreset(preset: PriorityPeriodPreset): void {
  if (preset === 'LAST_7_DAYS') {
    updateFilters({ preset, ...lastSevenDaysRange(new Date()) })
    return
  }
  // `CURRENT`/`PREVIOUS` são resolvidos pelo servidor: mandar um intervalo velho
  // junto faria o backend ignorar o `cycle` e a tela mentir sobre o período.
  updateFilters({ preset, startAt: null, endAt: null })
}

function onCustomRange(range: { start: Date | null; end: Date | null }): void {
  if (!range.start || !range.end) return
  updateFilters({
    preset: 'CUSTOM',
    startAt: range.start.toISOString(),
    endAt: range.end.toISOString()
  })
}

const toggleInList = <T,>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value]

const onToggleGroup = (value: string): void => {
  hasTouchedGroupFilter = true
  updateFilters({
    operationalGroups: toggleInList(props.filters.operationalGroups, value)
  })
}

/** Q36: trocar a unidade poda, no MESMO patch, o setor que ficou sem unidade. */
const onToggleUnit = (value: string): void => {
  const healthUnits = toggleInList(props.filters.healthUnits, Number(value))
  const sectors = prunedSectorsForUnits(props.filters.sectors, healthUnits, sectorOptions.value)
  updateFilters(sectors === props.filters.sectors ? { healthUnits } : { healthUnits, sectors })
}

const onToggleSector = (value: string): void => {
  updateFilters({ sectors: toggleInList(props.filters.sectors, Number(value)) })
}

const onTogglePosition = (value: string): void => {
  updateFilters({
    positions: togglePositionOption(positionOptions.value, props.filters.positions, value)
  })
}

/**
 * Carga isolada: falha aqui (ou service ausente) não pode impedir unidade,
 * escala e grupo de preencherem. Depois da carga, um link com setor de outra
 * unidade é podado (Review Focus 1).
 */
const loadPlaceOptions = async (): Promise<void> => {
  try {
    const options = await Network.priorityPayments.filterOptions()
    sectorOptions.value = options.sectors
    positionOptions.value = options.positions
    const pruned = prunedSectorsForUnits(
      props.filters.sectors,
      props.filters.healthUnits,
      options.sectors
    )
    if (pruned !== props.filters.sectors) updateFilters({ sectors: pruned })
  } catch (error) {
    notifyError(error, 'Não foi possível carregar setores e posições')
  }
}

const onToggleSchedule = (value: string): void => {
  updateFilters({ schedules: toggleInList(props.filters.schedules, Number(value)) })
}

const onCheck = (value: string): void => {
  updateFilters({ checkStatus: (value || null) as PriorityCheckStatus | null })
}

/**
 * Médico (apontamento 2): opções de `facets.doctors`. Um médico selecionado que
 * não veio nas facetas (link antigo, teto de 500) continua na lista para poder
 * ser desmarcado.
 */
const doctorSelectOptions = computed(() => {
  const known = new Set(props.doctorOptions.map((doctor) => doctor.doctorId))
  return [
    ...props.doctorOptions.map((doctor) => ({
      label: `${doctor.doctorName} (${doctor.count})`,
      value: String(doctor.doctorId)
    })),
    ...props.filters.doctorIds
      .filter((id) => !known.has(id))
      .map((id) => ({ label: `Médico #${id}`, value: String(id) }))
  ]
})

const onToggleDoctor = (value: string): void => {
  updateFilters({ doctorIds: toggleInList(props.filters.doctorIds, Number(value)) })
}

/** Data do plantão (`s.date`): o picker devolve meia-noite LOCAL; `toDateOnly` não passa por UTC. */
const stationDateFilter = ref<{ reset?: () => void } | null>(null)

const stationDateRange = computed(() => {
  const start = parseLocalDate(props.filters.stationDateFrom)
  const end = parseLocalDate(props.filters.stationDateTo)
  return start && end ? { start, end } : null
})

const dayMonth = (isoDay: string): string => `${isoDay.slice(8, 10)}/${isoDay.slice(5, 7)}`

const stationDateTitle = computed(() => {
  const { stationDateFrom: from, stationDateTo: to } = props.filters
  if (!from || !to) return 'Data do plantão'
  return `Data do plantão: ${dayMonth(from)}–${dayMonth(to)}`
})

function onStationDate(range: { start: Date | null; end: Date | null }): void {
  if (!range.start || !range.end) return
  updateFilters({ stationDateFrom: toDateOnly(range.start), stationDateTo: toDateOnly(range.end) })
}

function onClearStationDate(): void {
  stationDateFilter.value?.reset?.()
  updateFilters({ stationDateFrom: null, stationDateTo: null })
}

function isSituationKnownByServer(situation: PrioritySituation): boolean {
  return !!props.situationCounts && situation in props.situationCounts
}

/** Situação (Q22, DO-18): o "filtro secundário por situação", com contagem. */
const situationOptions = computed(() =>
  PRIORITY_SITUATION_GROUPS.filter(
    // Q1 — o DTO do backend valida `situations` por enum: antes do backend conhecer
    // NOT_REQUIRED, oferecer o filtro daria 400. Só aparece quando os counters trazem a chave.
    (group) => group.key !== 'notRequired' || isSituationKnownByServer('NOT_REQUIRED')
  ).map((group) => {
    const counts = props.situationCounts
    const total = counts
      ? group.situations.reduce((sum, situation) => sum + (counts[situation] ?? 0), 0)
      : null
    return { label: total === null ? group.label : `${group.label} (${total})`, value: group.key }
  })
)

const selectedSituationKeys = computed(() =>
  PRIORITY_SITUATION_GROUPS.filter((group) =>
    group.situations.every((situation) => props.filters.situations.includes(situation))
  ).map((group) => group.key)
)

function onToggleSituationGroup(key: string): void {
  const group = PRIORITY_SITUATION_GROUPS.find((candidate) => candidate.key === key)
  if (!group) return
  updateFilters({ situations: toggleGroup(props.filters.situations, group.situations) })
}

/** Estado do SLA (Q22): multi, com "Não iniciado" e o atalho "Todos os pendentes". */
const SLA_STATE_OPTIONS = [
  ...PRIORITY_SLA_STATES.map((state) => ({
    label: PRIORITY_SLA_STATE_LABELS[state],
    value: state
  })),
  { label: 'Todos os pendentes', value: PENDING_OPTION }
]

const hasAllPending = (states: PrioritySlaState[]): boolean =>
  PRIORITY_SLA_PENDING_STATES.every((state) => states.includes(state))

const selectedSlaValues = computed(() => [
  ...props.filters.slaStates,
  ...(hasAllPending(props.filters.slaStates) ? [PENDING_OPTION] : [])
])

function onToggleSlaState(value: string): void {
  const current = props.filters.slaStates
  updateFilters({
    slaStates:
      value === PENDING_OPTION
        ? toggleGroup(current, PRIORITY_SLA_PENDING_STATES)
        : toggleInList(current, value as PrioritySlaState)
  })
}

/**
 * Q23 — quando a pessoa só enxerga UM grupo operacional, a fila já abre
 * recortada nele. Quem infere o grupo é a página (a partir das linhas da
 * primeira resposta sem filtro de grupo): nem a listagem de escalas nem
 * `/field-options/operational-groups` (tabela global) sabem o grupo da pessoa.
 *
 * Pré-seleciona uma única vez por montagem, e só se a URL não trouxe grupo e a
 * pessoa ainda não mexeu no filtro: um link compartilhado ou uma escolha
 * explícita mandam mais do que a heurística.
 */
let hasPreselectedGroup = false
let hasTouchedGroupFilter = false

watch(
  () => props.inferredOperationalGroup,
  (group) => {
    if (!group || hasPreselectedGroup || hasTouchedGroupFilter) return
    if (props.filters.operationalGroups.length) return
    hasPreselectedGroup = true
    updateFilters({ operationalGroups: [group] })
  },
  { immediate: true }
)

onMounted(async () => {
  // Cargas independentes: uma falha não pode impedir a outra de preencher. A
  // store do ds-attendance não relança; o erro fica em `filterOptionsLoadError`.
  const [, groups] = await Promise.all([
    dsAttendanceStore.fetchFilterOptions().then(() => {
      if (dsAttendanceStore.filterOptionsLoadError)
        notifyErrorMessage(dsAttendanceStore.filterOptionsLoadError)
    }),
    new FieldOptionsService().getOperationalGroups().catch((error) => {
      notifyError(error, 'Não foi possível carregar os grupos operacionais')
      return []
    }),
    loadPlaceOptions()
  ])

  groupOptions.value = groups.map((group) => ({ label: group.name, value: group.name }))
})

/**
 * Busca com debounce: cada tecla dispararia uma listagem pesada no BE (e um
 * `router.replace`). O texto fica local enquanto a pessoa digita — o `:value`
 * preso a `filters.search` seria sobrescrito por qualquer re-render antes do
 * debounce — e só sobe depois de `SEARCH_DEBOUNCE_MS` sem digitar.
 */
const searchText = ref(props.filters.search)
let searchDebounce: ReturnType<typeof setTimeout> | null = null

const cancelSearchDebounce = (): void => {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = null
}

function onSearchInput(value: string): void {
  searchText.value = value
  cancelSearchDebounce()
  searchDebounce = setTimeout(() => {
    searchDebounce = null
    updateFilters({ search: value })
  }, SEARCH_DEBOUNCE_MS)
}

// Mudança de fora (limpar filtros, URL): o campo acompanha e a busca pendente cai.
watch(
  () => props.filters.search,
  (search) => {
    if (search === searchText.value) return
    cancelSearchDebounce()
    searchText.value = search
  }
)

onBeforeUnmount(cancelSearchDebounce)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "flex flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-lg border border-lightGray30 bg-white p-3",
    "data-testid": _ctx.testId
  }, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createElementVNode("div", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(PRIORITY_PERIOD_PRESETS), (preset) => {
          return (_openBlock(), _createElementBlock("button", {
            key: preset.value,
            type: "button",
            class: _normalizeClass(["rounded-full border px-2.5 py-1 text-[13px]", chipClass(_ctx.filters.preset === preset.value)]),
            "aria-pressed": _ctx.filters.preset === preset.value,
            "data-testid": `${_ctx.testId}-preset-${preset.value}`,
            onClick: ($event: any) => (onPreset(preset.value))
          }, _toDisplayString(preset.label), 11, _hoisted_5))
        }), 128))
      ]),
      (_ctx.cycleLabel)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: "font-mono text-xs text-neutral50",
            title: listedUntilText.value ?? undefined,
            "data-testid": `${_ctx.testId}-cycle-label`
          }, [
            _createTextVNode(_toDisplayString(_ctx.cycleLabel) + " ", 1),
            (listedUntilText.value)
              ? (_openBlock(), _createElementBlock("span", {
                  key: 0,
                  "data-testid": `${_ctx.testId}-listed-until`
                }, _toDisplayString(listedUntilText.value), 9, _hoisted_7))
              : _createCommentVNode("", true)
          ], 8, _hoisted_6))
        : _createCommentVNode("", true),
      (_ctx.filters.preset === 'CUSTOM')
        ? (_openBlock(), _createBlock(DateFilter, {
            key: 1,
            title: "Período",
            "is-range": true,
            "model-value": customRange.value,
            "data-testid": `${_ctx.testId}-custom-range`,
            onSelect: onCustomRange
          }, null, 8, ["model-value", "data-testid"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(SelectFilter, {
      label: "Grupo operacional",
      "is-open": openFilter.value === 'group',
      options: groupOptions.value,
      multiple: true,
      searchable: true,
      "selected-value": _ctx.filters.operationalGroups,
      "data-testid": `${_ctx.testId}-group`,
      onToggle: _cache[0] || (_cache[0] = ($event: any) => (toggleOpen('group'))),
      onSelect: onToggleGroup
    }, null, 8, ["is-open", "options", "selected-value", "data-testid"]),
    _createVNode(SelectFilter, {
      label: "Unidade",
      "is-open": openFilter.value === 'unit',
      options: unitOptions.value,
      multiple: true,
      searchable: true,
      "selected-value": _ctx.filters.healthUnits.map(String),
      "data-testid": `${_ctx.testId}-unit`,
      onToggle: _cache[1] || (_cache[1] = ($event: any) => (toggleOpen('unit'))),
      onSelect: onToggleUnit
    }, null, 8, ["is-open", "options", "selected-value", "data-testid"]),
    _createVNode(SelectFilter, {
      label: "Setor",
      "is-open": openFilter.value === 'sector',
      options: sectorSelectOptions.value,
      multiple: true,
      searchable: true,
      "selected-value": _ctx.filters.sectors.map(String),
      "data-testid": `${_ctx.testId}-sector`,
      onToggle: _cache[2] || (_cache[2] = ($event: any) => (toggleOpen('sector'))),
      onSelect: onToggleSector
    }, null, 8, ["is-open", "options", "selected-value", "data-testid"]),
    _createVNode(SelectFilter, {
      label: "Posição",
      "is-open": openFilter.value === 'position',
      options: positionSelectOptions.value,
      multiple: true,
      searchable: true,
      "selected-value": selectedPositions.value,
      "data-testid": `${_ctx.testId}-position`,
      onToggle: _cache[3] || (_cache[3] = ($event: any) => (toggleOpen('position'))),
      onSelect: onTogglePosition
    }, null, 8, ["is-open", "options", "selected-value", "data-testid"]),
    _createVNode(SelectFilter, {
      label: "Escala",
      "is-open": openFilter.value === 'schedule',
      options: scheduleOptions.value,
      multiple: true,
      searchable: true,
      "selected-value": _ctx.filters.schedules.map(String),
      "data-testid": `${_ctx.testId}-schedule`,
      onToggle: _cache[4] || (_cache[4] = ($event: any) => (toggleOpen('schedule'))),
      onSelect: onToggleSchedule
    }, null, 8, ["is-open", "options", "selected-value", "data-testid"]),
    _createVNode(SelectFilter, {
      label: "Médico",
      "is-open": openFilter.value === 'doctor',
      options: doctorSelectOptions.value,
      multiple: true,
      searchable: true,
      "selected-value": _ctx.filters.doctorIds.map(String),
      "data-testid": `${_ctx.testId}-doctor`,
      onToggle: _cache[5] || (_cache[5] = ($event: any) => (toggleOpen('doctor'))),
      onSelect: onToggleDoctor
    }, null, 8, ["is-open", "options", "selected-value", "data-testid"]),
    _createElementVNode("div", _hoisted_8, [
      _createVNode(DateFilter, {
        ref_key: "stationDateFilter",
        ref: stationDateFilter,
        title: stationDateTitle.value,
        "is-range": true,
        "model-value": stationDateRange.value,
        "data-testid": `${_ctx.testId}-station-date`,
        onSelect: onStationDate
      }, null, 8, ["title", "model-value", "data-testid"]),
      (_ctx.filters.stationDateFrom || _ctx.filters.stationDateTo)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            class: "rounded-full border border-lightGray30 px-2 py-0.5 text-xs text-neutral50",
            "aria-label": "Limpar data do plantão",
            "data-testid": `${_ctx.testId}-station-date-clear`,
            onClick: onClearStationDate
          }, " × ", 8, _hoisted_9))
        : _createCommentVNode("", true)
    ]),
    _createVNode(SelectFilter, {
      label: "Situação",
      "is-open": openFilter.value === 'situation',
      options: situationOptions.value,
      multiple: true,
      "selected-value": selectedSituationKeys.value,
      "data-testid": `${_ctx.testId}-situation`,
      onToggle: _cache[6] || (_cache[6] = ($event: any) => (toggleOpen('situation'))),
      onSelect: onToggleSituationGroup
    }, null, 8, ["is-open", "options", "selected-value", "data-testid"]),
    _createVNode(SelectFilter, {
      label: "Estado do SLA",
      "is-open": openFilter.value === 'slaState',
      options: SLA_STATE_OPTIONS,
      multiple: true,
      "selected-value": selectedSlaValues.value,
      "data-testid": `${_ctx.testId}-sla-state`,
      onToggle: _cache[7] || (_cache[7] = ($event: any) => (toggleOpen('slaState'))),
      onSelect: onToggleSlaState
    }, null, 8, ["is-open", "selected-value", "data-testid"]),
    _createVNode(SelectFilter, {
      label: "Check",
      "is-open": openFilter.value === 'check',
      options: CHECK_OPTIONS,
      "selected-value": _ctx.filters.checkStatus ?? '',
      "data-testid": `${_ctx.testId}-check`,
      onToggle: _cache[8] || (_cache[8] = ($event: any) => (toggleOpen('check'))),
      onSelect: onCheck
    }, null, 8, ["is-open", "selected-value", "data-testid"]),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("label", {
        class: "text-[11px] uppercase tracking-wider text-neutral50",
        for: `${_ctx.testId}-search`
      }, " Médico ou ID ", 8, _hoisted_11),
      _createElementVNode("input", {
        id: `${_ctx.testId}-search`,
        type: "search",
        placeholder: "Buscar",
        class: "w-44 rounded-md border border-lightGray30 px-2 py-1 text-[13px] text-textPrimary",
        value: searchText.value,
        maxlength: "200",
        "data-testid": `${_ctx.testId}-search`,
        onInput: _cache[9] || (_cache[9] = ($event: any) => (onSearchInput(($event.target as HTMLInputElement).value)))
      }, null, 40, _hoisted_12)
    ]),
    _createElementVNode("div", _hoisted_13, [
      _hoisted_14,
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("button", {
          type: "button",
          class: "rounded-full border border-lightGray30 bg-white px-2.5 py-1 text-[13px] text-neutral50 disabled:cursor-not-allowed disabled:opacity-60",
          title: "Exporta a lista (xlsx)",
          "aria-label": `Antecipe pago sem horário: ${antecipePaidUnconfirmedLabel.value}. Exporta a lista (xlsx)`,
          disabled: _ctx.isExporting || !_ctx.antecipePaidUnconfirmedCount,
          "aria-busy": _ctx.isExporting ? 'true' : undefined,
          "data-testid": `${_ctx.testId}-quick-antecipe-unconfirmed`,
          onClick: _cache[10] || (_cache[10] = ($event: any) => (emit('export-antecipe-paid-unconfirmed')))
        }, [
          _createTextVNode(" Antecipe pago sem horário "),
          _createElementVNode("b", {
            class: "tabular-nums",
            "data-testid": `${_ctx.testId}-quick-antecipe-unconfirmed-count`
          }, _toDisplayString(antecipePaidUnconfirmedLabel.value), 9, _hoisted_17)
        ], 8, _hoisted_16),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleQuickFilters.value, (quick) => {
          return (_openBlock(), _createElementBlock("button", {
            key: quick.key,
            type: "button",
            class: _normalizeClass(["rounded-full border px-2.5 py-1 text-[13px]", chipClass(quick.isOn(_ctx.filters))]),
            "aria-pressed": quick.isOn(_ctx.filters),
            "data-testid": `${_ctx.testId}-quick-${quick.key}`,
            onClick: ($event: any) => (updateFilters(quick.toggle(_ctx.filters)))
          }, _toDisplayString(quick.label), 11, _hoisted_18))
        }), 128))
      ])
    ]),
    _hoisted_19,
    _createElementVNode("div", _hoisted_20, [
      (_ctx.lastLoadedLabel)
        ? (_openBlock(), _createElementBlock("span", _hoisted_21, " Atualizado " + _toDisplayString(_ctx.lastLoadedLabel) + " · automático a cada 60 s ", 1))
        : _createCommentVNode("", true),
      _createElementVNode("button", {
        type: "button",
        class: "rounded-md border border-lightGray30 px-3 py-1.5 text-[13px] font-medium text-textPrimary",
        disabled: _ctx.isRefreshing,
        "data-testid": `${_ctx.testId}-refresh`,
        onClick: _cache[11] || (_cache[11] = ($event: any) => (emit('refresh')))
      }, " Atualizar ", 8, _hoisted_22),
      _createElementVNode("button", {
        type: "button",
        class: "rounded-md border border-primary bg-primary px-3 py-1.5 text-[13px] font-medium text-white",
        disabled: _ctx.isExporting,
        "data-testid": `${_ctx.testId}-export`,
        onClick: _cache[12] || (_cache[12] = ($event: any) => (emit('export')))
      }, " Exportar xlsx ", 8, _hoisted_23)
    ])
  ], 8, _hoisted_1))
}
}

})